'use strict';

namespace Search {

    class UnifiedSearch {
        // Attr
        searchType: UnifiedSearchType;
        fid: string;
        hiddenQueryString: string;
        elements;
        trackDefaultParameters: string;
        clearForm: string;
        overrideAllowNoDatesYet: string;
        isFlexibleTripType: boolean;
        disableResetOptions;

        // data
        private model: UnifiedSearchModel;
        private enabledStartDates: any;
        private minStartDate: string;
        private cabinOptions: Cabin[];
        private currencyOptions: string[];
        private airlineOptions: Airline[];
        private durations: number[];
        private IsRebook: boolean;

        private areaName: string;
        private showDatePicker: boolean = false;
        private showOccupancyPicker: boolean = false;
        //private setFocusDestination: boolean = false;

        private occupancyConfig: OccupancyConfig;
        private occupancyCaption: string;

        private destinationLabel: string;
        private destinationPlaceholder: string;
        private searchTypeName: string;

        constructor (
            private $route: ng.route.IRouteService,
            private $routeParams: ng.route.IRouteParamsService,
            private $q: ng.IQService,
            private $scope,
            private broker: Core.Broker,
            private searchUnitService: SearchUnitService,
            private unifiedSearchService: UnifiedSearchService,
            private searchQueryString: SearchQueryString,
            private destinationService,
            private occupancyService: Core.OccupancyService,
            private holidayDeals,
            private dateUtil,
            private animationUtil,
            private $timeout: ng.ITimeoutService,
            private $location: ng.ILocationService
        ) {
            this.cabinOptions = searchUnitService.getCabinOptions();
            this.currencyOptions = searchUnitService.getCurrencyOptions();
            this.airlineOptions = searchQueryString.allAirlines;
            this.IsRebook = !!$routeParams.BookingRef;
        };

        init (elements): void {
            this.elements = elements;

            this.model = this.unifiedSearchService.getDefaultModel();

            this.setSearchType();

            // watch search type
            this.$scope.$watch('us.searchType', (val, old) => {
                if (val !== old) {
                    this.setSearchType();
                }
            });

            this.$scope.$watchCollection('us.model.destination', _.debounce(val => {
                // Watch is debounced because autocompleter selection is causing two updates.
                if (val && val[0]) {
                    // This call mutates the destination
                    this.destinationService.getDetails(val[0]).then(dest => {
                        if (this.searchType !== UnifiedSearchType.Hotels) {
                            this.setEnabledStartDates(this.model.destination, this.model.airports);
                        }
                    });
                }
                this.setEnabledDurations();
            }, 200));

            this.$scope.$watchCollection('us.model.airports', val => {
                if (val && val[0]) {
                    this.setEnabledStartDates(this.model.destination, this.model.airports);
                }
            });

            this.$scope.$watchCollection('us.model.occupancy', val => {
                if (val && val[0]) {
                    this.occupancyCaption = this.occupancyService.getOccupancyString(val);
                }
            });

            this.$scope.$watchCollection('us.model.airlines', val => {
                if (val.length > 1 ) {
                    // This search unit only supports a single airline
                    // selection, so just keep the first option.
                    this.model.airlines.splice(1, 1);
                }
                if (val[0] == null) {
                    // This is result of selecting the "All airlines" option from
                    // the select box control - just remove it.
                    this.model.airlines.splice(0, 1);
                }
            });

            this.$scope.$watchGroup(['us.model.startDate', 'us.model.endDate'], ([startDate, endDate]) => {
                if (startDate && !endDate) {
                    let index = 1;
                    switch (this.searchType) {
                        case UnifiedSearchType.Hotels:
                            index = 0;
                            break;
                        case UnifiedSearchType.Transfers:
                            index = 2;
                            break;
                        case UnifiedSearchType.Marhaba:
                            index = 3;
                            break;
                    }
                    this.searchUnitService.endDate(index, null);
                    this.searchUnitService.startDate(index, startDate);
                    this.model.endDate = this.searchUnitService.endDate(index);
                }
            });

            if (this.clearForm !== 'true') {
                // prepopulate the model
                this.unifiedSearchService.getQueryStringValues(this.hiddenQueryString).then(result => {
                    let queryStringValues = result;
                    this.unifiedSearchService
                        .prepopulateModel(this.model, queryStringValues, this.disableResetOptions)
                        .then(model => {
                            if (queryStringValues.autoSearch) {
                                this.$timeout(() => {
                                    this.search(true);
                                    this.clearParams();
                                }, 1000);
                            }
                    });
                });
            }
        }

        private clearParams = () => {
            //cleardown query params - as otherwise params will override values search unit values when not necessary
            this.$location.replace()
                .search('startDate', null)
                .search('endDate', null)
                .search('depAir', null)
                .search('occ', null)
                .search('autoSearch', null)
                .search('cabin', null);
        }

        private setSearchType(): void {
            this.$scope.$broadcast('flash:hide');

            // get "area" associated with this search type
            this.areaName = this.unifiedSearchService.getAreaName(this.searchType);

            // get occupancy parameters
            this.occupancyConfig = this.searchUnitService.getOccupancyConfig(this.areaName);

            // get min start date for date range picker
            this.minStartDate = this.searchUnitService.getMinStartDate(this.areaName);

            if(this.$route.current && this.$route.current['$$route']) {
                this.$route.current['$$route'].area = this.areaName;
            }

            // set labels
            this.setLabels();

            this.setEnabledStartDates(this.model.destination, this.model.airports);
        }

        private setEnabledStartDates(destination: any[], departureAirports: any[]): ng.IPromise<any>  {
            if (this.flightIncluded() && departureAirports[0] && destination[0]) {
                // get enabled dates
                let dest = destination[0];
                let DestinationAirportCodes = (dest.Code || dest.DestinationAirports || '').split(',');
                if (DestinationAirportCodes && DestinationAirportCodes.length) {
                    let request = {
                        DepartureAirportCodes: _.map(departureAirports, 'Code'),
                        DestinationAirportCodes
                    };
                    return this.searchUnitService.loadOutboundDates(request)
                        .then(dates => {
                            this.enabledStartDates = dates;
                            return dates;
                        });
                }
            }
            this.enabledStartDates = undefined;
            return this.$q.when(null);
        }

        private setEnabledDurations(): void {
            // if (!this.model.destination[0]) {
            //     // limit to holiday deal durations
            //     this.durations = _.clone(this.holidayDeals.DURATIONS);
            // } else {
            //    this.durations.length = 0;
            //}
            this.durations = [];
        }

        private setDuration(): void {
            let start = this.model.startDate,
                end = this.model.endDate;
            this.model.duration = start && end ? this.dateUtil.numberOfNights(start, end) : 0;
        }

        private setEndDate(): void {
            const d = this.model.duration;
            if (this.model.startDate && d && d > 0) {
                const start = this.dateUtil.parseDate(this.model.startDate);
                this.model.endDate = this.dateUtil.addDays(start, d, true);
            }
        }

        private setLabels(): void {
            this.destinationLabel = this.hotelIncluded() ?
                'UNIFIEDSEARCHDESTINATIONLABEL' : 'UNIFIEDSEARCHDESTINATIONAIRPORTLABEL';

            this.destinationPlaceholder = this.hotelIncluded() ?
                'UNIFIEDSEARCHDESTINATIONPLACEHOLDER' : 'UNIFIEDSEARCHDESTINATIONAIRPORTPLACEHOLDER';

            this.searchTypeName = this.unifiedSearchService.getAreaName(this.searchType);
        }

        public getHotelSearchButtonLabel(): string {
            return this.searchType === UnifiedSearchType.Hotels && this.model.noDatesYet ?
                'FINDHOTELS' : 'SEARCHBUTTONTEXT';
        }

        public search(isAutoSearch?, isFlexibleTripType?): void {
            let dest;
            return this.destinationService.getDetails(this.model.destination[0])
                .then(d => {
                    dest = d;
                    if (this.searchType !== UnifiedSearchType.Hotels) {
                        return this.setEnabledStartDates([dest], this.model.airports);
                    }
                })
                .then(() => {
                    //creating a copy of the model to use to run a search.
                    const destination = _.assign({}, this.model.destination[0], dest);
                    let modelCopy = <UnifiedSearchModel>_.assign(<UnifiedSearchModel>{}, this.model, {
                        destination: [destination]
                    });
                    modelCopy.onewayonly = this.oneWayFlightIncluded() && this.model.onewayonly;
                    this.unifiedSearchService.launchSearch(
                        modelCopy,
                        this.searchType,
                        this.fid,
                        !isAutoSearch,
                        false,
                        isFlexibleTripType
                    );
                });
        }

        public reset(): void {
            // reset the search unit
            this.model = this.unifiedSearchService.getDefaultModel();
        }

        public selectDestination() {
        }

        public selectDateRange() {
            this.setDuration();
            this.showDatePicker = false;
        }

        public openDatePicker(event): void {
            this.showDatePicker = true;
            this.openPicker(event, $('.date-range-picker'), 12, 0);
        }
        public openOccupancyPicker(event): void {
            this.showOccupancyPicker = true;
            this.openPicker(event, $('.occupancy-picker'), -47, -(2*event.target.getBoundingClientRect().height)-15);
        }

        // common function for occupancyPicjer and DatePicker
        public openPicker(event, picker, adjust, pixelsForHeightExpand): void {

            if ( this.hotelOnly() ) {
                picker.addClass('hotelOnlyCalendar');
            } else {
                picker.removeClass('hotelOnlyCalendar');
            }
            picker.css( {
                'opacity': '0'
            });

            setTimeout( function() {
                        const winHeight = $(window).height();
                        const evTop = event.target.getBoundingClientRect().top;
                        const eventHeight = event.target.getBoundingClientRect().height;
                        const evTopPlusWin = evTop + picker.outerHeight() + eventHeight +
                                                Math.abs(pixelsForHeightExpand);

                        if ( evTopPlusWin > winHeight ) {
                            let scrollAdjust = 0;
                            if ( window.innerHeight > picker.outerHeight() ) {
                                scrollAdjust = window.innerHeight - picker.outerHeight() -
                                                $('.integrated-search-unit').height();
                            }
                            scrollAdjust += pixelsForHeightExpand;

                            $('html,body').animate(
                                {'scrollTop': $('.integrated-search-unit').offset().top-scrollAdjust + 'px'},
                                {'complete': function() {
                                    picker.css( {
                                            'top': adjust + event.target.getBoundingClientRect().top + 'px',
                                            'opacity': '1'
                                        });
                                    }
                                }
                            );

                        } else {
                            picker.css( {
                                'top': adjust + event.target.getBoundingClientRect().top + 'px',
                                'opacity': '1'
                            });
                        }
                }, 50 );

            $(window).resize( function() {
                picker.css( {
                    'top': adjust + event.target.getBoundingClientRect().top + 'px'
                });
            });

        }

        public resetDestination() {
            this.searchUnitService.setDestination(null, null, null);
            this.searchUnitService.setDestinationAirport(null);
            this.model.destination = [];
        }

        public oneWayFlightIncluded(): boolean {
            return TRPCONFIG.Domain.OneWayFlightsEnabled && this.searchType === UnifiedSearchType.Flights;
        }

        public flightIncluded(): boolean {
            return this.searchType === UnifiedSearchType.Flights || this.searchType === UnifiedSearchType.Holidays;
        }

        public hotelIncluded(): boolean {
            return this.searchType === UnifiedSearchType.Hotels || this.searchType === UnifiedSearchType.Holidays;
        }

        public hotelOnly(): boolean {
            return this.searchType === UnifiedSearchType.Hotels;
        }

        public flexibleDatesDisabled(): boolean {
            return TRPCONFIG.Domain2.FlexibleDatesFlowsDisabled.indexOf(UnifiedSearchType[this.searchType]) !== -1;
        }
    }

    angular
        .module('search')
        .directive('unifiedSearch', (): ng.IDirective => {
            return {
                scope: {},
                controller: UnifiedSearch,
                controllerAs: 'us',
                bindToController: {
                    searchType: '=',
                    fid: '=?',
                    hiddenQueryString: '@',
                    trackDefaultParameters: '@',
                    clearForm: '@',
                    overrideAllowNoDatesYet: '@',
                    isFlexibleTripType: '=?',
                    disableResetOptions: '@?'
                },
                templateUrl: 'partials/search/unifiedSearch',
                replace: true,
                restrict: 'E',
                link: (
                    scope: ng.IScope,
                    el: ng.IAugmentedJQuery,
                    attr: ng.IAttributes,
                    controller: UnifiedSearch
                ) => {
                    const elements = {
                        container: el
                    };
                    controller.init(elements);
                }
            };
        });
}
